import { SelectMenuItem } from 'bold-ui'
import { SelectDownshiftMenuProps } from 'bold-ui/lib/components/Select/SelectSingle/SelectDownshiftMenu'
import { CustomSelectFieldProps, SelectField } from 'components/form/core'
import { CidSelectFieldDocument } from 'graphql/hooks.generated'
import { Cid10QueryInput, CidSelectFieldQuery, CidSelectFieldQueryVariables } from 'graphql/types.generated'
import { useCallback, useMemo, useState } from 'react'
import { CypressProps } from 'types/cypress'
import { dedupById, emptyArrayOf, excludeIds } from 'util/array'

import { HeaderMeta } from '../model-motivoPrescricaoSelect'
import { RenderGrupoSelectHeader } from '../RenderGrupoSelectHeader'
import { DEFAULT_SELECT_PAGE_PARAM, useAsyncQuerySelect } from '../useAsyncQuerySelect'
import { buildItems, filterRegistradosByQuery } from '../utils-motivoPrescricaoSelect'
import { cidItemToString, cidRenderItem, CidSelectFieldModel } from './CidSelectField'

type CidItem = NonNullable<CidSelectFieldModel>

type MotivoPrescricaoCidSelectFieldProps = CustomSelectFieldProps<CidSelectFieldModel> &
  Pick<Cid10QueryInput, 'sexo' | 'idadeCidadaoEmAnos'> &
  CypressProps & {
    itensRegistrados: ReadonlyArray<CidSelectFieldModel>
  }

const ID_GRUPO_REGISTRADOS = 'grupoCidsRegistrados'
const ID_GRUPO_OUTROS = 'grupoOutrosCids'

const makeHeaderItem = (id: string, meta: HeaderMeta): CidItem =>
  ({ id, ciapsRelacionadas: [], codigo: '', nome: '', ...meta } as CidItem & HeaderMeta)

const getCidSearchableFields = (item: CidItem): ReadonlyArray<string> => [item.nome, item.codigo]

const pageParams = {
  ...DEFAULT_SELECT_PAGE_PARAM,
  sort: ['descricao'],
}

const MotivoPrescricaoCidItem = (
  itemProps: SelectDownshiftMenuProps<CidSelectFieldModel> & { item: CidSelectFieldModel; index: number }
) => {
  const {
    index,
    downshift: { getItemProps, highlightedIndex },
    item,
    renderItem,
  } = itemProps

  if (item?.id === ID_GRUPO_REGISTRADOS || item?.id === ID_GRUPO_OUTROS) {
    const { headerTitulo, headerEmptyMessage } = item as CidSelectFieldModel & HeaderMeta
    return (
      <RenderGrupoSelectHeader
        titulo={headerTitulo}
        emptyMessage={headerEmptyMessage}
        itemProps={getItemProps({ item, disabled: true })}
      />
    )
  }

  return (
    <SelectMenuItem selected={highlightedIndex === index} {...getItemProps({ item })}>
      {renderItem?.(item)}
    </SelectMenuItem>
  )
}

export function MotivoPrescricaoCidSelectField(props: Readonly<MotivoPrescricaoCidSelectFieldProps>) {
  const { sexo, idadeCidadaoEmAnos, itensRegistrados, dataTestId, ...rest } = props

  const [inputQuery, setInputQuery] = useState<string>('')

  const {
    selectProps: { items, onFilterChange: onFilterChangeBase, onFocus: onFocusBase, ...asyncPropsRest },
  } = useAsyncQuerySelect<CidSelectFieldModel, CidSelectFieldQuery, CidSelectFieldQueryVariables>({
    query: CidSelectFieldDocument,
    extractItems: (data) => data?.cids?.content?.filter((it): it is CidItem => it != null),
    variables: (inputQuery: string): CidSelectFieldQueryVariables => ({
      input: {
        query: inputQuery,
        sexo,
        idadeCidadaoEmAnos,
        somenteRelacionados: false,
        pageParams,
      },
      fetchCiapsRelacionadas: false,
    }),
  })

  const onFilterChange = useCallback<NonNullable<typeof onFilterChangeBase>>(
    (inputString, downshift) => {
      setInputQuery(inputString ?? '')
      onFilterChangeBase?.(inputString, downshift)
    },
    [onFilterChangeBase]
  )

  const onFocus = useCallback<NonNullable<typeof onFocusBase>>(
    (event) => {
      setInputQuery('')
      onFocusBase?.(event)
    },
    [onFocusBase]
  )

  const registrados = useMemo(
    () => (itensRegistrados ?? emptyArrayOf<CidSelectFieldModel>()).filter((it): it is CidItem => it != null),
    [itensRegistrados]
  )

  const registradoIds = useMemo(() => new Set(registrados.map((it) => it.id)), [registrados])

  const filteredRegistrados = useMemo(() => filterRegistradosByQuery(registrados, inputQuery, getCidSearchableFields), [
    registrados,
    inputQuery,
  ])

  const menuItems = useMemo<CidItem[]>(() => {
    const outros = excludeIds(dedupById(items ?? emptyArrayOf<CidSelectFieldModel>()), registradoIds)

    const headerRegistrados = makeHeaderItem(ID_GRUPO_REGISTRADOS, {
      headerTitulo: 'Registrados nesse atendimento',
      headerEmptyMessage: filteredRegistrados.length === 0 && 'Nenhum resultado encontrado',
    })

    const headerOutros = makeHeaderItem(ID_GRUPO_OUTROS, {
      headerTitulo: 'Outros',
      headerEmptyMessage: outros.length === 0 && 'Nenhum resultado encontrado',
    })

    return buildItems(headerRegistrados, filteredRegistrados, headerOutros, outros, false)
  }, [items, registradoIds, filteredRegistrados])

  return (
    <SelectField<CidSelectFieldModel>
      itemToString={cidItemToString}
      items={menuItems}
      renderItem={cidRenderItem}
      components={{
        Item: MotivoPrescricaoCidItem,
      }}
      dataTestId={dataTestId}
      onFilterChange={onFilterChange}
      onFocus={onFocus}
      {...asyncPropsRest}
      {...rest}
    />
  )
}
